import { createSlice } from "@reduxjs/toolkit";

export interface LobType {
  id: number;
  is_active: boolean;
  updated_at: string;
  lob_name: string;
  display_name: string;
  created_at: string;
}

export interface MasterState {
  userData: Array<{ id: number; name: string; email: string }>;
  lobList: LobType[];
  lobOptions: Array<{ value: string; label: string }>;
  memberList: any[];
  memberOptions: Array<{ value: string; label: string }>;
  memberOptionsNested: NestedMemberOption[];
}

export interface NestedMemberOption {
  id: string | number;
  name: string;
  full_name?: string;
  email?: string;
  role_name?: string;
  manager_name?: string;
  manager_id?: string | number | null;
  children: NestedMemberOption[];
}

const initialState: MasterState = {
  userData: [],
  lobList: [],
  lobOptions: [],
  memberList: [],
  memberOptions: [],
  memberOptionsNested: [],
};

export function buildNestedMemberOptions(payload: unknown): NestedMemberOption[] {
  const response = payload as any;
  const members = Array.isArray(response)
    ? response
    : Array.isArray(response?.results)
      ? response.results
      : Array.isArray(response?.data)
        ? response.data
        : [];

  const nodes = new Map<string, NestedMemberOption>();

  members.forEach((member: any) => {
    if (member?.id === undefined || member?.id === null) return;

    const fullName =
      member?.employee?.full_name ||
      member?.full_name ||
      member?.employee?.email ||
      member?.email ||
      "Unknown";

    nodes.set(String(member.id), {
      ...member,
      id: member.id,
      name: fullName,
      children: [],
    });
  });

  members.forEach((member: any) => {
    const managerId = member?.manager_id;
    if (managerId === undefined || managerId === null || nodes.has(String(managerId))) return;

    nodes.set(String(managerId), {
      id: managerId,
      name: member?.manager_name || `Manager ${managerId}`,
      children: [],
    });
  });

  const childIds = new Set<string>();
  members.forEach((member: any) => {
    if (member?.id === undefined || member?.id === null) return;

    const node = nodes.get(String(member.id));
    const managerId = member?.manager_id;
    const parent =
      managerId === undefined || managerId === null ? undefined : nodes.get(String(managerId));

    if (!node || !parent || String(managerId) === String(member.id)) return;

    parent.children.push(node);
    childIds.add(String(member.id));
  });

  return Array.from(nodes.values()).filter((node) => !childIds.has(String(node.id)));
}

const masterSlice = createSlice({
  name: "masterSlice",
  initialState,
  reducers: {
    userData: (state, action) => {
      state.userData = action.payload;
    },
    setLobList: (state, action: { payload: LobType[] }) => {
      state.lobList = action.payload;
      state.lobOptions = action.payload.map((lob) => ({
        value: String(lob.id),
        label: lob.display_name,
      }));
    },
    setMemberList: (state, action: { payload: any }) => {
      const members = Array.isArray(action.payload)
        ? action.payload
        : Array.isArray(action.payload?.results)
          ? action.payload.results
          : Array.isArray(action.payload?.data)
            ? action.payload.data
            : [];

      state.memberList = members;
      state.memberOptions = members.map((member) => ({
        value: String(member.id),
        label:
          member?.employee?.full_name ||
          member?.employee?.email ||
          member?.full_name ||
          member?.email ||
          "Unknown",
      }));
    },

    setMemberOptionNested: (state, action: { payload: any }) => {
      state.memberOptionsNested = buildNestedMemberOptions(action.payload);
    },
  },
});

export const { userData, setLobList, setMemberList, setMemberOptionNested } = masterSlice.actions;
export default masterSlice.reducer;
